import {
  UserManager,
  WebStorageStateStore,
  User as OidcUser
} from "oidc-client-ts";
import {
  LOGIN_AUTHORITY_URL,
  LOGIN_REDIRECT_URL,
  LOUGOUT_URL,
  LOGIN_CLIENT_ID,
  LOGIN_AUTHORITY_SCOPE,
  SILENT_URL
} from "utils/constants";
import { postVistoriaLogin, postVistoriaLoginCohab } from "services/va";
import { postEcLogin } from "services/ec";
import { exigeNovoLogin } from "./errosOidc";

class AuthService {
  constructor() {
    const settings = {
      authority: LOGIN_AUTHORITY_URL,
      client_id: LOGIN_CLIENT_ID,
      redirect_uri: LOGIN_REDIRECT_URL,
      post_logout_redirect_uri: LOUGOUT_URL,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      response_type: "code",
      scope: LOGIN_AUTHORITY_SCOPE,
      automaticSilentRenew: false,
      silent_redirect_uri: SILENT_URL,
      response_mode: "query",
      revokeTokensOnSignout: true,
      clockSkewInSeconds: 36000,
      // Padrões que mudaram no oidc-client-ts; mantidos como no oidc-client 1.x.
      loadUserInfo: true,
      monitorSession: true,
      includeIdTokenInSilentRenew: true,
      revokeTokenTypes: ["access_token"]
    };
    this.userManager = new UserManager(settings);

    this.eventTokenExpired = false;
  }

  tokenExpired = async () => {
    if (!this.eventTokenExpired) {
      this.eventTokenExpired = true;

      this.getEvents().addAccessTokenExpired(() => {
        this.renew()
          .then()
          .catch(async res => {
            this.getEvents().removeAccessTokenExpired(res);
            this.eventTokenExpired = false;
            if (exigeNovoLogin(res)) {
              await this.reiniciarLogin();
            } else {
              await this.login();
            }
          });
      });
    }
  };

  login = async () => {
    return this.userManager.signinRedirect();
  };

  renew = async () => {
    return this.userManager.signinSilent();
  };

  renewCallback = async () => {
    return this.userManager.signinSilentCallback();
  };

  logout = async () => {
    await this.userManager.signoutRedirect({
      id_token_hint: localStorage.getItem("id_token")
    });
  };

  signoutRedirectCallback = async () => {
    return new Promise((accept, rej) => {
      this.userManager
        .signoutRedirectCallback()
        .then(res => {
          this.userManager.removeUser();
          this.userManager.clearStaleState();
          accept(res);
        })
        .catch(err => {
          rej(err);
        });
    });
  };

  getUser = async () => {
    return this.getSoftAuthUser() || this.userManager.getUser();
  };

  getEvents = () => {
    return this.userManager.events;
  };

  loginCallback = async () => {
    return this.userManager.signinRedirectCallback();
  };

  clear = async () => {
    return this.userManager.clearStaleState();
  };

  // token management

  SOFT_AUTHORIZATION_KEY = "SOFT_AUTHORIZATION";

  removeSoftAuthorizationToken = () => {
    localStorage.removeItem(this.SOFT_AUTHORIZATION_KEY);
  };

  removeOidcAuthorization = () => {
    // Coleta antes de remover: remover durante a iteração por índice desloca
    // as chaves e pula a seguinte, deixando `oidc.*` órfão no storage.
    const chavesOidc = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const chave = localStorage.key(i);
      if (chave && chave.substring(0, 5) === "oidc.") {
        chavesOidc.push(chave);
      }
    }
    chavesOidc.forEach(chave => localStorage.removeItem(chave));
  };

  // Descarta usuário e estados do oidc-client e inicia um login novo. Usado
  // quando a sessão do IdS venceu (login_required e afins) ou quando o estado
  // local ficou órfão/inválido — casos que pedem login, não tela de erro.
  reiniciarLogin = async () => {
    try {
      await this.userManager.removeUser();
    } catch (e) {
      console.error("Erro ao remover usuário do oidc-client", e);
    }
    try {
      await this.userManager.clearStaleState();
    } catch (e) {
      console.error("Erro ao limpar estado do oidc-client", e);
    }
    this.removeOidcAuthorization();
    return this.login();
  };

  clearUserStorage = async () => {
    this.removeSoftAuthorizationToken();
    this.removeOidcAuthorization();
    sessionStorage.clear();
    return this.userManager.clearStaleState();
  };

  completeLoginVistoriador = async (data, cpf, email) => {
    await this.clearUserStorage();
    await this.setOidcUser(data.token);
    this.setSoftAuthorizationToken(
      JSON.stringify({
        va: true,
        ...JSON.parse(data.softAuthToken),
        access_token: JSON.parse(data.token).access_token,
        cpf,
        email
      })
    );
  };

  completeLoginVistoriadorCohab = async (
    data,
    cpf,
    email,
    nome,
    numeroOcorrencia
  ) => {
    await this.clearUserStorage();
    await this.setOidcUser(data.token);
    this.setSoftAuthorizationToken(
      JSON.stringify({
        va: true,
        cohab: true,
        ...JSON.parse(data.softAuthToken),
        access_token: JSON.parse(data.token).access_token,
        cpf,
        email,
        nome,
        numeroOcorrencia
      })
    );
  };

  completeLoginEC = async (data, cpf, email) => {
    await this.clearUserStorage();
    await this.setOidcUser(data.token);
    this.setSoftAuthorizationToken(
      JSON.stringify({
        ec: true,
        ...JSON.parse(data.softAuthToken),
        access_token: JSON.parse(data.token).access_token,
        cpf,
        email
      })
    );
  };

  setOidcUser = async userString => {
    const user = JSON.parse(userString);
    const userObj = new OidcUser(user);
    await this.userManager.storeUser(userObj);
  };

  setSoftAuthorizationToken = token => {
    localStorage.setItem(this.SOFT_AUTHORIZATION_KEY, token);
  };

  getSoftAuthorizationToken = () => {
    try {
      return JSON.parse(localStorage.getItem(this.SOFT_AUTHORIZATION_KEY));
    } catch (e) {
      return null;
    }
  };

  getSoftAuthUser = () => {
    const softAuthToken = this.getSoftAuthorizationToken();

    if (!softAuthToken) return null;

    return {
      isSoftauth: true,
      ...softAuthToken
    };
  };

  renewSoftAuth = async () => {
    const user = this.getSoftAuthUser();
    if (!user || !user.cpf || !user.email) return null;

    try {
      let response;
      if (user.va && user.cohab) {
        response = await postVistoriaLoginCohab(
          user.cpf,
          user.email,
          user.nome,
          user.numeroOcorrencia
        );
      } else if (user.va) {
        response = await postVistoriaLogin(user.cpf, user.email);
      } else if (user.ec) {
        response = await postEcLogin(user.cpf, user.email);
      }

      if (response && response.data) {
        if (user.va && user.cohab) {
          await this.completeLoginVistoriadorCohab(
            response.data,
            user.cpf,
            user.email,
            user.nome,
            user.numeroOcorrencia
          );
        } else if (user.va) {
          await this.completeLoginVistoriador(
            response.data,
            user.cpf,
            user.email
          );
        } else {
          await this.completeLoginEC(response.data, user.cpf, user.email);
        }
        return this.getSoftAuthUser();
      }
    } catch (error) {
      console.error("Error renewing soft auth token", error);
      return null;
    }
    return null;
  };

  clearLocalStorageSoft = async () => {
    await this.clearUserStorage();
  };
}

export default new AuthService();
